import React, { useState } from 'react';
import { Gift, Check, Mail, User, Cake, Heart, Snowflake, Sparkles } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';

const TIERS = [50, 100, 200];
const OCCASIONS = [
  { key: 'classic', label: 'Just Because', icon: Sparkles, blurb: 'Timeless black & gold' },
  { key: 'birthday', label: 'Birthday', icon: Cake, blurb: 'Many happy returns' },
  { key: 'thankyou', label: 'Thank You', icon: Heart, blurb: 'With gratitude' },
  { key: 'holiday', label: 'Holiday', icon: Snowflake, blurb: "Season's greetings" },
];

export default function GiftCards() {
  const { addToCart, openDrawer } = useCart();
  const { toast } = useToast();
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [deliverTo, setDeliverTo] = useState('recipient');
  const [deliverOn, setDeliverOn] = useState('');
  const [occasion, setOccasion] = useState('classic');
  const todayISO = new Date().toISOString().slice(0, 10);

  const finalAmount = useCustom ? Number(custom) : amount;

  const add = () => {
    if (!finalAmount || finalAmount < 25 || finalAmount > 1000) {
      toast({ title: 'Pick an amount between $25 and $1,000', variant: 'destructive' }); return;
    }
    if (deliverTo === 'recipient' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipientEmail.trim())) {
      toast({ title: "Enter the recipient's email", description: 'Or choose "Send it to me" to pass it on yourself.', variant: 'destructive' }); return;
    }
    addToCart({
      id: `giftcard-${Date.now()}`,
      name: `Digital Gift Card — $${Math.round(finalAmount)}`,
      code: 'GIFT', size: '', price: Math.round(finalAmount * 100) / 100, image: '',
      stockStatus: 'Digital', digital: true,
      giftCard: { recipientName: recipientName.trim(), recipientEmail: recipientEmail.trim(), message: message.trim(), deliverTo, occasion, deliverOn },
    });
    toast({ title: 'Gift card added to cart 🎁' });
    openDrawer();
  };

  return (
    <div className="bg-alp-black min-h-screen" data-testid="gift-cards-page">
      <div className="border-b border-alp-line">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 text-center">
          <p className="text-alp-gold text-xs tracking-[0.3em] uppercase mb-3">Give the gift of luxe</p>
          <h1 className="font-serif text-5xl md:text-6xl text-alp-cream heading-glow">Digital Gift Cards</h1>
          <p className="text-alp-gray mt-4 max-w-xl mx-auto">Emailed instantly once paid, redeemable as store credit on anything in the boutique. No expiry, ever.</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 md:px-8 py-14">
        <div className="border border-alp-gold/30 rounded-xl bg-alp-card/40 p-7 space-y-7">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">1 · Choose an amount</p>
            <div className="flex flex-wrap gap-2">
              {TIERS.map((t) => (
                <button key={t} onClick={() => { setAmount(t); setUseCustom(false); }} data-testid={`gift-tier-${t}`}
                  className={`px-6 py-3 rounded border text-sm font-serif transition-colors ${!useCustom && amount === t ? 'bg-alp-gold text-alp-black border-alp-gold' : 'border-alp-line text-alp-cream hover:border-alp-gold'}`}>
                  ${t}
                </button>
              ))}
              <div className={`flex items-center gap-2 px-4 py-2 rounded border ${useCustom ? 'border-alp-gold' : 'border-alp-line'}`}>
                <span className="text-alp-gray text-sm">$</span>
                <input type="number" min="25" max="1000" value={custom} placeholder="Custom"
                  onChange={(e) => { setCustom(e.target.value); setUseCustom(true); }}
                  onFocus={() => setUseCustom(true)} data-testid="gift-custom-amount"
                  className="w-24 bg-transparent text-alp-cream text-sm outline-none" />
              </div>
            </div>
            <p className="text-alp-gray text-[11px] mt-2">Custom amounts from $25 to $1,000.</p>
          </div>

          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">2 · Pick a design</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="gift-occasions">
              {OCCASIONS.map((o) => (
                <button key={o.key} onClick={() => setOccasion(o.key)} data-testid={`gift-occasion-${o.key}`}
                  className={`flex flex-col items-center gap-1.5 border rounded-lg px-3 py-4 transition-colors ${occasion === o.key ? 'border-alp-gold bg-alp-gold/10' : 'border-alp-line hover:border-alp-gold/50'}`}>
                  <o.icon size={18} className={occasion === o.key ? 'text-alp-gold' : 'text-alp-gray'} />
                  <span className={`text-xs ${occasion === o.key ? 'text-alp-gold' : 'text-alp-cream/80'}`}>{o.label}</span>
                  <span className="text-alp-gray text-[10px]">{o.blurb}</span>
                </button>
              ))}
            </div>
            <p className="text-alp-gray text-[11px] mt-2">Sets the look and greeting of the gift card email.</p>
          </div>

          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">3 · Who is it for?</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="relative">
                <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-alp-gray" />
                <input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="Recipient's name" data-testid="gift-recipient-name"
                  className="w-full bg-alp-dark border border-alp-line rounded pl-9 pr-3 py-3 text-sm text-alp-cream focus:border-alp-gold outline-none" />
              </div>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-alp-gray" />
                <input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="Recipient's email" data-testid="gift-recipient-email"
                  className="w-full bg-alp-dark border border-alp-line rounded pl-9 pr-3 py-3 text-sm text-alp-cream focus:border-alp-gold outline-none" />
              </div>
            </div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} maxLength={400}
              placeholder="Add a personal message (optional) — it appears on the gift card email" data-testid="gift-message"
              className="w-full mt-3 bg-alp-dark border border-alp-line rounded px-3 py-3 text-sm text-alp-cream focus:border-alp-gold outline-none resize-y" />
          </div>

          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">4 · Delivery</p>
            <div className="space-y-2">
              {[['recipient', 'Email it straight to them once my payment clears'], ['buyer', "Send it to me — I'll pass it on myself"]].map(([v, l]) => (
                <label key={v} className={`flex items-center gap-3 border rounded px-4 py-3 cursor-pointer transition-colors ${deliverTo === v ? 'border-alp-gold bg-alp-gold/5' : 'border-alp-line'}`} data-testid={`gift-deliver-${v}`}>
                  <input type="radio" name="deliverTo" checked={deliverTo === v} onChange={() => setDeliverTo(v)} className="accent-alp-gold" />
                  <span className="text-alp-cream/90 text-sm">{l}</span>
                </label>
              ))}
            </div>
            <div className="mt-3 border border-alp-line rounded px-4 py-3">
              <label className="text-alp-cream/90 text-sm flex flex-wrap items-center gap-3">
                <span>Deliver on a specific date <span className="text-alp-gray text-xs">(optional — perfect for birthdays ahead)</span></span>
                <input type="date" value={deliverOn} min={todayISO} onChange={(e) => setDeliverOn(e.target.value)} data-testid="gift-deliver-date"
                  className="bg-alp-dark border border-alp-line rounded px-3 py-2 text-sm text-alp-cream focus:border-alp-gold outline-none [color-scheme:dark]" />
                {deliverOn && <button onClick={() => setDeliverOn('')} data-testid="gift-deliver-date-clear" className="text-alp-gray text-xs underline underline-offset-4 hover:text-alp-gold">Clear</button>}
              </label>
              <p className="text-alp-gray text-[11px] mt-1.5">{deliverOn ? `The gift card email will be held and delivered on ${new Date(deliverOn + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })} (Sydney time).` : 'Leave blank to send as soon as your payment clears.'}</p>
            </div>
          </div>

          <button onClick={add} data-testid="gift-add-to-cart"
            className="btn-glow w-full inline-flex items-center justify-center gap-2 bg-alp-gold text-alp-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">
            <Gift size={16} /> Add ${finalAmount || 0} gift card to cart
          </button>

          <div className="space-y-1.5 text-alp-gray text-xs">
            {['Delivered by email as soon as the order is marked paid', 'Redeemed on the Account page — the full amount becomes store credit', 'Spend it across as many orders as you like · no expiry'].map((t) => (
              <p key={t} className="flex items-center gap-2"><Check size={12} className="text-alp-gold shrink-0" /> {t}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
